import { aggregateNodeCosts, type CostTotals } from './costs';
import type { TemplateGraph } from './graph';
import { buildRuntimeTree, type RuntimeTree } from './instances';
import { calculateProgress, type ProgressSnapshot } from './progress';
import { resolveAllResults } from './result';
import type { AnswerSet, ConditionResult, NodeId } from './types';
import { walkVisibility, type VisibilityResult } from './visibility';

/**
 * Eén doorrekening van het hele dossier. De publieke functies zijn hier dunne
 * schillen omheen, zodat vijf aanroepen op dezelfde antwoorden één berekening
 * kosten in plaats van vijf.
 */
export interface EngineSnapshot {
    readonly graph: TemplateGraph;
    readonly answers: AnswerSet;
    readonly tree: RuntimeTree;
    readonly visibility: VisibilityResult;
    readonly results: ReadonlyMap<NodeId, ConditionResult>;
    readonly costs: CostTotals;
    readonly progress: ProgressSnapshot;
}

/**
 * De cache houdt per graaf één uitkomst vast, op identiteit van de antwoordset.
 * Daarom moet een antwoordset onveranderlijk zijn: schrijf bij elke wijziging
 * een nieuw object, dan rekent de engine opnieuw.
 */
const cache = new WeakMap<TemplateGraph, { answers: AnswerSet; snapshot: EngineSnapshot }>();

export function computeSnapshot(graph: TemplateGraph, answers: AnswerSet): EngineSnapshot {
    const cached = cache.get(graph);

    if (cached && cached.answers === answers) {
        return cached.snapshot;
    }

    const snapshot = buildSnapshot(graph, answers);

    cache.set(graph, { answers, snapshot });

    return snapshot;
}

function buildSnapshot(graph: TemplateGraph, answers: AnswerSet): EngineSnapshot {
    const tree = buildRuntimeTree(graph, answers);
    const visibility = walkVisibility(graph, answers, tree);
    const results = resolveAllResults(tree, visibility);
    const costs = aggregateNodeCosts(graph, answers, tree, visibility);
    const progress = calculateProgress(visibility, results);

    return { graph, answers, tree, visibility, results, costs, progress };
}
